<script setup lang="ts">
import { tracking } from '~/services/tracking.client'
import { marketingContext } from '~/services/marketing.client'
import { computed, reactive, ref } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { phoneValidator } from "~/services/helpers";
import { useMessages } from "~/composables/useMessages";

type ContactPayload = {
  name: string;
  phone: string;
  email: string;
  subject: string;
  message: string;
  website: string;
};

const { t } = useI18n();

const settingsStore = useSettingsStore();
const { settings } = storeToRefs(settingsStore);

const authStore = useAuthStore();
const { user, fullName } = storeToRefs(authStore);

const { show_error, show_success } = useMessages();

const formRef = ref<FormInstance>();
const loading = ref(false);

const form = reactive<ContactPayload>({
  name: fullName.value || "",
  phone: user.value?.phone || "",
  email: user.value?.email || "",
  subject: "",
  message: "",
  website: "",
});

const rules = computed<FormRules<ContactPayload>>(() => ({
  name: [
    {
      required: true,
      message: t("name_required"),
      trigger: ["blur", "change"],
    },
    { min: 2, message: t("name_invalid"), trigger: ["blur", "change"] },
  ],
  email: [
    {
      required: true,
      message: t("email_required"),
      trigger: ["blur", "change"],
    },
    { type: "email", message: t("email_invalid"), trigger: ["blur", "change"] },
  ],
  phone: [{ validator: phoneValidator, trigger: ["blur", "change"] }],
  subject: [
    {
      required: true,
      message: t("subject_required"),
      trigger: ["blur", "change"],
    },
    { min: 3, message: t("subject_invalid"), trigger: ["blur", "change"] },
  ],
  message: [
    {
      required: true,
      message: t("message_required"),
      trigger: ["blur", "change"],
    },
    {
      min: 10,
      message: t("message_invalid", { charts: 10 }),
      trigger: ["blur", "change"],
    },
  ],
}));

const turnstile = ref<{ reset?: () => void } | null>(null);
const turnstileToken = ref("");
const captchaEnabled = computed(() => settings.value?.cf_enable !== false);

const siteKey = computed(() =>
  !captchaEnabled.value
    ? ""
    : import.meta.dev
    ? "1x00000000000000000000AA"
    : (settings.value?.cf_site_key ?? ""),
);

async function submit() {
  if (!formRef.value || loading.value) return;

  const ok = await formRef.value.validate().catch(() => false);
  if (!ok) return;

  if (captchaEnabled.value && !turnstileToken.value) {
    return;
  }

  try {
    loading.value = true;

    const payload: ContactPayload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      subject: form.subject.trim(),
      message: form.message.trim(),
      website: form.website,
    };

    const { $api } = useNuxtApp();

    const receipt = await $api<{ tracking?: { event_id: string } }>("/contact", {
      method: "POST",
      body: { ...payload, token: turnstileToken.value, marketing_tracking: marketingContext() },
    });

    if (receipt?.tracking?.event_id) tracking.lead(receipt.tracking.event_id);
    await show_success(t("message_sent"));
    reset();
  } catch (err: any) {
    await show_error(err);
    turnstile.value?.reset?.();
    turnstileToken.value = "";
  } finally {
    loading.value = false;
  }
}

function reset() {
  formRef.value?.resetFields();
  form.website = "";
  turnstile.value?.reset?.();
  turnstileToken.value = "";
}
const map = settings.value.gmap || settings.value.address;
</script>

<template>
  <div class="my-5 row justify-content-center">
    <div class="col-12">
      <h2 class="section_title mb-4">{{ $t("send_inquiry") }}</h2>
    </div>
    <div class="col">
      <div class="contact_form">
        <el-form
          ref="formRef"
          :model="form"
          :rules="rules"
          label-position="top"
          @submit.prevent="submit"
        >
          <div class="contact-honeypot" aria-hidden="true">
            <label for="contact-website">Website</label>
            <input
              id="contact-website"
              v-model="form.website"
              name="website"
              type="text"
              tabindex="-1"
              autocomplete="off"
            >
          </div>

          <div class="row g-4">
            <div class="col-12 col-md-6">
              <el-form-item :label="t('name')" prop="name">
                <el-input
                  v-model="form.name"
                  size="large"
                  :placeholder="t('name')"
                  autocomplete="name"
                  clearable
                  :minlength="2"
                  class="custom_inputs"
                >
                  <template #prefix>
                    <icon name="lets-icons:user"></icon>
                  </template>
                </el-input>
              </el-form-item>
            </div>

            <div class="col-12 col-md-6">
              <el-form-item :label="t('email')" prop="email">
                <el-input
                  v-model="form.email"
                  size="large"
                  :placeholder="t('email')"
                  autocomplete="email"
                  inputmode="email"
                  clearable
                  class="custom_inputs"
                >
                  <template #prefix>
                    <icon name="lets-icons:e-mail"></icon>
                  </template>
                </el-input>
              </el-form-item>
            </div>

            <div class="col-12 col-md-6">
              <el-form-item :label="t('phone')" prop="phone">
                <el-input
                  v-model="form.phone"
                  size="large"
                  :placeholder="t('phone')"
                  autocomplete="tel"
                  inputmode="tel"
                  clearable
                  class="custom_inputs"
                >
                  <template #prefix>
                    <icon name="lets-icons:phone"></icon>
                  </template>
                </el-input>
              </el-form-item>
            </div>

            <div class="col-12 col-md-6">
              <el-form-item :label="t('subject')" prop="subject">
                <el-input
                  v-model="form.subject"
                  size="large"
                  :placeholder="t('subject')"
                  clearable
                  :minlength="3"
                  class="custom_inputs"
                >
                  <template #prefix>
                    <icon name="lets-icons:info"></icon>
                  </template>
                </el-input>
              </el-form-item>
            </div>

            <div class="col-12">
              <el-form-item :label="t('message')" prop="message">
                <el-input
                  v-model="form.message"
                  type="textarea"
                  :rows="7"
                  :placeholder="t('message')"
                  maxlength="2000"
                  :minlength="10"
                  show-word-limit
                  resize="none"
                  class="custom_inputs"
                />
              </el-form-item>
            </div>

            <div class="col-12">
              <div class="d-lg-flex align-items-center justify-content-between">
                <div class="captcha-wrap">
                  <NuxtTurnstile
                    v-if="captchaEnabled && siteKey"
                    ref="turnstile"
                    v-model="turnstileToken"
                    :site-key="siteKey"
                  />
                </div>
                <div
                  class="actions d-flex flex-column flex-sm-row gap-2 justify-content-end"
                >
                  <el-button round @click="reset" :disabled="loading">
                    {{ t("reset") }}
                  </el-button>

                  <el-button
                    round
                    type="primary"
                    @click="submit"
                    :loading="loading"
                    :disabled="captchaEnabled && !turnstileToken"
                    class="ms-lg-2 ms-0 mt-lg-0 mt-3"
                  >
                    {{ t("send_message") }}
                  </el-button>
                </div>
              </div>
            </div>
            <div class="col-12 mt-2">
              <div class="helper">
                {{ t("contact_helper") }}
              </div>
            </div>
          </div>
        </el-form>
      </div>
    </div>
    <div class="col-lg-5 column-map pt-4">
      <ClientOnly>
        <iframe
          v-if="map"
          :src="
            'https://maps.google.com/maps?width=100%25&height=400&hl=en&q=' +
            map +
            '&t=&z=14&ie=UTF8&iwloc=B&output=embed'
          "
          class="gmap"
          style="border: 0"
          :allowfullscreen="true"
          loading="lazy"
        ></iframe>
      </ClientOnly>
    </div>
  </div>
</template>

<style scoped>
.actions {
  margin-top: 4px;
}

.captcha-wrap {
  display: flex;
  justify-content: flex-end;
}

.contact-honeypot {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.helper {
  opacity: 0.7;
  font-size: var(--fs-sm);
}
.gmap {
  width: 100%;
  aspect-ratio: 4/3;
  height: auto;
}
</style>
